import { IconArrowRotaryLastLeft, IconArrowRotaryLeft } from '@tabler/icons-react'
import { Loader2, RefreshCw, Shield, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useSiweAuth } from '@/hooks/useSiweAuth'
import { useWalletConnection } from '@/hooks/useWalletConnection'

interface SiweAuthButtonProps {
	className?: string
	variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
	size?: 'default' | 'sm' | 'lg' | 'icon'
	children?: React.ReactNode
	onSuccess?: () => void
	onError?: () => void
	customLabel?: string
}

export function SiweAuthButton({
	className,
	variant = 'default',
	size = 'default',
	children,
	onSuccess,
	onError,
	customLabel,
}: SiweAuthButtonProps) {
	const [isAuthenticating, setIsAuthenticating] = useState(false)
	const { isConnected, isDisconnected, handleConnect } = useWalletConnection()
	const { authenticate, isLoading, error, getAuthMethod } = useSiweAuth()

	const handleSiweAuth = async () => {
		if (isDisconnected) {
			// First connect the wallet
			handleConnect()
			return
		}

		if (!isConnected) {
			return
		}

		try {
			setIsAuthenticating(true)
			const result = await authenticate()

			if (result.success) {
				onSuccess?.()
			} else {
				onError?.()
			}
		} catch (error) {
			onError?.()
		} finally {
			setIsAuthenticating(false)
		}
	}

	const getButtonText = () => {
		// If custom label is provided, use it (but still show loading state)
		if (customLabel && !isAuthenticating && !isLoading) {
			return customLabel
		}

		if (isDisconnected) {
			return 'Connect Wallet'
		}

		if (isAuthenticating || isLoading) {
			return 'Authenticating...'
		}

		const authMethod = getAuthMethod()
		if (authMethod === 'wallet') {
			return 'Sign with Wallet'
		} else if (authMethod === 'social') {
			return 'Sign with Account'
		}

		return 'Authenticate'
	}

	const getButtonIcon = () => {
		if (isAuthenticating || isLoading) {
			return <Loader2 className="w-4 h-4 animate-spin" />
		}

		const authMethod = getAuthMethod()
		if (authMethod === 'wallet') {
			return <ShieldCheck className="w-4 h-4" />
		} else if (authMethod === 'social') {
			return <ShieldCheck className="w-4 h-4" />
		}

		return <Shield className="w-4 h-4" />
	}

	return (
		<div className="space-y-2">
			<Button
				onClick={handleSiweAuth}
				disabled={isAuthenticating || isLoading}
				className={className}
				variant={variant}
				size={size}
			>
				{getButtonIcon()}
				<span className="ml-2">{children || getButtonText()}</span>
			</Button>

			{error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
		</div>
	)
}
